import { useShoppingCart } from "@sushicorp/contexts";
import { buildArtisnHeaders } from "@sushicorp/services";
import { redeemCoupon } from "artisn/shopping-cart";
import { Benefit } from "artisn/types";
import dayjs from "dayjs";
import Link from "next/link";
import { useRouter } from "next/router";
import React, { useState } from "react";

import Styles from "./ProfileCoupons.styles";
import { ProfileCouponsProps as Props } from "./ProfileCoupons.types";
import RedeemCouponInput from "components/Cart/RedeemCoupon/RedeemCouponInput/RedeemCouponInput";
import BenefitsList from "components/coupons/BenefitsList/BenefitsList";
import Button from "components/global/Button/Button";
import Footer from "components/global/Footer/Footer";
import GoBack from "components/global/GoBack/GoBack";
import Navbar from "components/global/Navbar/Navbar";
import ProfileNavigation from "components/global/ProfileNavigation/ProfileNavigation";
import CONSTANTS from "config/constants";
import useAuth from "contexts/auth/auth.context.hooks";
import useI18n from "hooks/useI18n";
import { createSuccessNotification } from "utils/notifications.utils";

import EmptySVG from "../../../../public/assets/images/empty-benefit-list.svg";

const { SHOPPING_CART_DEFAULT_NAME, ACCOUNT_ID } = CONSTANTS.ARTISN;
const { API_URL } = CONSTANTS.API;
const { WITH_BOTTOM_SEARCH } = CONSTANTS.FEATURE_FLAGS;
const { WITH_BOTTOM_RESPONSIVE_BUTTONS } = CONSTANTS.FEATURE_FLAGS;

const ProfileCoupons: React.FC<Props> = props => {
  const { isAnonymous = false, uid } = useAuth();
  const { push } = useRouter();
  const t = useI18n();
  const { benefitsWallet } = useShoppingCart();
  const [redeemCouponError, setRedeemCouponError] = useState("");
  const [selectedBenefit, setSelectedBenefit] = useState<Benefit>();
  const [couponCode, setCouponCode] = useState("");
  const { benefits } = benefitsWallet ?? {};
  const availableBenefits = benefits?.filter(item =>
    dayjs().isBefore(item.expirationDate)
  );
  const redeemCouponInputDisabled = !benefits || isAnonymous;

  const redeemHandler = async (code: string) => {
    if (code.trim() === "") return;
    if (!uid) return;

    try {
      setRedeemCouponError("");
      await redeemCoupon(
        {
          shoppingCartName: SHOPPING_CART_DEFAULT_NAME,
          apiURL: API_URL,
          accountId: ACCOUNT_ID,
          anonymous: isAnonymous,
          customerId: uid
        },
        code,
        await buildArtisnHeaders()
      );
      setCouponCode("");
      createSuccessNotification(t.coupons.redeemSuccess);
    } catch (e) {
      if (e.message.includes("Cupón ya canjeado")) {
        return setRedeemCouponError(e.message);
      }

      const walletError =
        "Failed to fetch benefits from firestore for the current customer after redeeming coupon";
      if (e.message.includes(walletError)) {
        return createSuccessNotification(t.coupons.redeemSuccess);
      }

      setRedeemCouponError(`${t.errors.coupons.invalid}`);
    }
  };

  /**
   * Function to apply a coupon to the shopping cart.
   *
   * @since 1.0.0
   */
  // TODO: uncomment if needed
  // const applyToCartHandler = async () => {
  //   if (!selectedBenefit || !shoppingCart) return;

  //   try {
  //     const { benefitId, type } = selectedBenefit;
  //     if (type === "PRODUCT") {
  //       setTemporalBenefit(selectedBenefit);
  //       goToBenefitDetail(selectedBenefit);
  //       return;
  //     }

  //     if (
  //       type === "ALTER_DELIVERY" ||
  //       type === "DISCOUNT_FIXED" ||
  //       type === "DISCOUNT_PERCENTAGE"
  //     ) {
  //       setTemporalBenefit(undefined);
  //       if (!getShoppingCartProducts(shoppingCart).length) {
  //         throw new Error(
  //           "Can't apply benefit because there are no products in the cart"
  //         );
  //       }

  //       await applyBenefit(
  //         {
  //           benefitId,
  //           latitude: lat,
  //           longitude: lng,
  //           shippingCost,
  //           shoppingCartName: SHOPPING_CART_DEFAULT_NAME,
  //           anonymous: isAnonymous,
  //           shoppingCartId: shoppingCart.id,
  //           apiURL: API_URL
  //         },
  //         await buildArtisnHeaders()
  //       );
  //     }
  //   } catch (e) {
  //     console.error(e.message);
  //   }
  // };

  const benefitsNode = (
    <BenefitsList
      showRemove={false}
      benefits={availableBenefits}
      selectedBenefit={selectedBenefit}
      setSelectedBenefit={setSelectedBenefit}
    />
  );

  const noBenefitsNode = (
    <div className="ProfileCoupons__empty">
      <EmptySVG />
      <p className="ProfileCoupons__empty__message">{t.coupons.noCoupons}</p>
    </div>
  );

  const signInRequestNode = (
    <div className="ProfileCoupons__request">
      <p className="ProfileCoupons__request__title">
        {t.coupons.registerAndGetBenefits}
      </p>
      <p className="ProfileCoupons__request__description">
        {t.coupons.getCoupons}
      </p>
      <Button
        className="ProfileCoupons__request__button"
        onClick={() => push("/signin")}
      >
        {t.coupons.enter}
      </Button>
    </div>
  );

  return (
    <Styles
      className="ProfileCoupons"
      withBottomSearch={WITH_BOTTOM_SEARCH}
      withBottomResponsiveButtons={WITH_BOTTOM_RESPONSIVE_BUTTONS}
    >
      <header className="ProfileCoupons__header">
        <Navbar className="ProfileCoupons__navbar" showBrand />
      </header>
      <main className="ProfileCoupons__main">
        <ProfileNavigation className="ProfileCoupons__profile-navigation" />
        <div className="ProfileCoupons__container">
          <GoBack className="ProfileOrders__go-back" title={t.common.goBack} />
          <div className="ProfileCoupons__coupons">
            <div className="ProfileCoupons__coupons__header">
              <h2 className="ProfileCoupons__coupons__header__title">
                {t.coupons.title}
              </h2>
              {isAnonymous ? (
                <div className="ProfileCoupons__coupons__header__subtitle">
                  <p className="ProfileCoupons__coupons__header__subtitle--first">
                    {t.coupons.enterWithYourAccount}
                  </p>
                  <Link href="/signin" legacyBehavior>
                    <a className="ProfileCoupons__coupons__header__subtitle--second">
                      {t.coupons.signIn}
                    </a>
                  </Link>
                </div>
              ) : null}

              <div className="ProfileCoupons__coupons__redeem">
                <RedeemCouponInput
                  disabled={redeemCouponInputDisabled}
                  couponCode={couponCode}
                  setCouponCode={setCouponCode}
                  onRedeem={redeemHandler}
                />
                {redeemCouponError ? (
                  <p className="ProfileCoupons__redeem-error">
                    {redeemCouponError}
                  </p>
                ) : null}
              </div>
            </div>
            {!isAnonymous ? (
              <>
                {benefits?.length ? (
                  <div className="ProfileCoupons__content">
                    <h2 className="ProfileCoupons__section">
                      {t.coupons.available}
                    </h2>
                    {benefitsNode}
                    {/* // TODO: uncomment if needed */}
                    {/* <Button
                      className="ProfileCoupons__apply-button"
                      type="FILLED"
                      color="primary"
                      disabled={!selectedBenefit}
                      onClick={applyToCartHandler}
                    >
                      {t.coupons.applyInCart}
                    </Button> */}
                  </div>
                ) : (
                  noBenefitsNode
                )}
              </>
            ) : (
              signInRequestNode
            )}
          </div>
        </div>
      </main>
      <footer className="ProfileCoupons__footer">
        <Footer />
      </footer>
    </Styles>
  );
};

ProfileCoupons.defaultProps = {};

export default ProfileCoupons;
